import get from 'lodash.get'
import { normalizeFieldConfig, resolveFieldValue, findEdgeLabelConfig, resolveNodeType } from './fieldHelpers'

function findLevelConfig (levels, groupingValue) {
  if (!groupingValue || !Array.isArray(levels)) return null
  return levels.find(l => l.match === groupingValue) || null
}

/**
 * Discovers children of a tree node by checking:
 *   1. The standard children key (default "data")
 *   2. Any other array-valued properties whose items contain the grouping field
 */
function findChildren (item, childrenAccessor, groupingAccessor) {
  const children = []

  const standardChildren = get(item, childrenAccessor)
  if (Array.isArray(standardChildren)) {
    children.push(...standardChildren)
  }

  Object.keys(item).forEach(key => {
    if (key === childrenAccessor) return
    const val = item[key]
    if (Array.isArray(val) && val.length > 0 && val[0] && typeof val[0] === 'object') {
      const hasGrouping = val.some(v => get(v, groupingAccessor) != null)
      if (hasGrouping) {
        children.push(...val)
      }
    }
  })

  return children
}

function walkTree (item, levels, edgeLabels, config, parentNodeId, parentLevelConfig, nodeMap, edgeMap) {
  const { childrenAccessor, groupingAccessor, idAccessorDefault } = config
  const grouping = get(item, groupingAccessor)
  const levelConfig = findLevelConfig(levels, grouping)

  const idAccessor = levelConfig?.idAccessor || idAccessorDefault
  const rawId = get(item, idAccessor)
  const idSegment = `${levelConfig?.id || grouping || 'node'}-${rawId ?? nodeMap.size}`
  const nodeId = parentNodeId ? `${parentNodeId}/${idSegment}` : idSegment

  const levelIndex = levelConfig ? levels.indexOf(levelConfig) : -1

  const nameConfig = normalizeFieldConfig(levelConfig?.name)
  const descConfig = normalizeFieldConfig(levelConfig?.description)

  nodeMap.set(nodeId, {
    id: nodeId,
    type: resolveNodeType(levelConfig?.nodeType),
    position: { x: 0, y: 0 },
    data: {
      nameConfig,
      descriptionConfig: descConfig,
      nameValue: resolveFieldValue(item, nameConfig),
      descriptionValue: resolveFieldValue(item, descConfig),
      variant: levelConfig?.variant || 'default',
      color: levelConfig?.color || undefined,
      size: levelConfig?.size || 'md',
      levelId: levelConfig?.id || grouping,
      levelIndex: levelIndex >= 0 ? levelIndex : undefined,
      raw: item
    }
  })

  if (parentNodeId) {
    const edgeKey = `${parentNodeId}->${nodeId}`
    if (!edgeMap.has(edgeKey)) {
      const edge = {
        id: `e-${edgeKey}`,
        source: parentNodeId,
        target: nodeId
      }

      if (parentLevelConfig && levelConfig) {
        const labelConfig = findEdgeLabelConfig(edgeLabels, parentLevelConfig.id, levelConfig.id)
        if (labelConfig) {
          const rawLabel = get(item, labelConfig.accessor)
          if (rawLabel != null) {
            edge.label = rawLabel
            edge.data = { label: rawLabel, format: labelConfig.format }
            edge.type = 'labeled'
          }
        }
      }

      edgeMap.set(edgeKey, edge)
    }
  }

  const children = findChildren(item, childrenAccessor, groupingAccessor)
  children.forEach(child => {
    walkTree(child, levels, edgeLabels, config, nodeId, levelConfig, nodeMap, edgeMap)
  })
}

export function buildFromTree (items, levels, edgeLabels, {
  childrenAccessor = 'data',
  groupingAccessor = 'grouping',
  idAccessorDefault = 'groupId'
} = {}) {
  if (!Array.isArray(items) || !items.length) return { nodes: [], edges: [] }

  const nodeMap = new Map()
  const edgeMap = new Map()

  items.forEach(item => {
    walkTree(item, levels, edgeLabels, { childrenAccessor, groupingAccessor, idAccessorDefault }, null, null, nodeMap, edgeMap)
  })

  return {
    nodes: Array.from(nodeMap.values()),
    edges: Array.from(edgeMap.values())
  }
}
